import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";

interface LivePriceCalculatorProps {
  subModality: string;
  supplies: string;
  hours: number;
  persons: number;
  basePriceOverride?: number;
  pricingConfiguration?: any;
  includedServices?: { name: string; individualPrice: number }[];
}

const normalizeKey = (value: string) => String(value || "").trim().toLowerCase();

const resolvePricingValue = (value: any): { baseRate: number; extraRate: number; effectiveRate: number } | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === "number" || typeof value === "string") {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    return { baseRate: numericValue, extraRate: 0, effectiveRate: numericValue };
  }

  if (typeof value === "object") {
    const isPricingShape = ["base", "price", "rate", "extra"].some((key) => key in value);
    if (!isPricingShape) return null;

    const baseRate = Number(value.base ?? value.price ?? value.rate ?? 0);
    const extraRate = Number(value.extra ?? 0);

    if (!Number.isFinite(baseRate) || !Number.isFinite(extraRate)) return null;
    return { baseRate, extraRate, effectiveRate: baseRate + extraRate };
  }

  return null;
};

const resolveDynamicRate = (pricingConfiguration: any, subModality: string, supplies: string) => {
  if (!pricingConfiguration || typeof pricingConfiguration !== "object") return null;

  // Sub-modality pricing (e.g. Cooking, Salon) can be a plain rate or { base, extra }.
  if (pricingConfiguration.subModality && typeof pricingConfiguration.subModality === "object") {
    for (const [label, value] of Object.entries(pricingConfiguration.subModality)) {
      const labelText = String(label);
      if (subModality === labelText || subModality.includes(labelText) || labelText.includes(subModality)) {
        const pricingValue = resolvePricingValue(value);
        if (!pricingValue) return null;
        // Supplies ($5/hr) is a flat add-on to the subModality rate
        const hasSupplies = normalizeKey(supplies) === "yes";
        const suppliesExtra = hasSupplies ? 5 : 0;
        return {
          baseRate: pricingValue.baseRate,
          effectiveRate: pricingValue.effectiveRate + suppliesExtra,
          extraCharge: pricingValue.extraRate,
          hasExtra: pricingValue.extraRate > 0,
          isSubModalityPricing: true,
        };
      }
    }
  }

  // Supply-based pricing: get both "yes" and "no" rates
  const noConfig = pricingConfiguration["no"];
  const yesConfig = pricingConfiguration["yes"];

  const resolveRate = (config: any): number | null => {
    const directValue = resolvePricingValue(config);
    if (directValue) return directValue.effectiveRate;

    if (config && typeof config === "object") {
      const normalizedKey = normalizeKey(subModality);
      for (const [key, val] of Object.entries(config)) {
        if (normalizeKey(key) === normalizedKey) {
          const pricingValue = resolvePricingValue(val);
          return pricingValue?.effectiveRate ?? null;
        }
      }
    }
    return null;
  };

  const noRate = resolveRate(noConfig);
  if (noRate === null) return null;

  const hasSupplies = normalizeKey(supplies) === "yes";
  const yesRate = resolveRate(yesConfig);
  const suppliesSurcharge = hasSupplies && yesRate !== null ? yesRate - noRate : 0;

  return {
    baseRate: noRate,
    extraCharge: suppliesSurcharge,
    hasExtra: hasSupplies && suppliesSurcharge > 0,
    effectiveRate: noRate + suppliesSurcharge,
    isSubModalityPricing: false,
  };
};

const TAX_PERCENT = 12;

const LivePriceCalculator: React.FC<LivePriceCalculatorProps> = ({
  subModality,
  supplies,
  hours,
  persons,
  basePriceOverride,
  pricingConfiguration,
  includedServices,
}) => {
  // --- Individual Services from selected sub-modality ---
  const servicesTotal = useMemo(() => {
    if (!includedServices || includedServices.length === 0) return null;
    const total = includedServices.reduce((sum, s) => sum + (s.individualPrice || 0), 0);
    return { services: includedServices, total };
  }, [includedServices]);

  const priceDetails = useMemo(() => {
      const dynamicRate = resolveDynamicRate(pricingConfiguration, subModality, supplies);
      const baseRate = basePriceOverride ?? dynamicRate?.baseRate ?? null;
      if (baseRate === null) return null;
      const extraCharge = dynamicRate?.extraCharge ?? 0;
      const hasExtra = dynamicRate?.hasExtra ?? false;
      const isSubModalityPricing = dynamicRate?.isSubModalityPricing ?? false;
      // When supplies surcharge exists, bake it into baseRate display
      const displayRate = basePriceOverride ?? dynamicRate?.effectiveRate ?? baseRate;
     const billableHours = hours * persons;
     const subtotal = billableHours * displayRate;
     const taxAmount = (subtotal * TAX_PERCENT) / 100;
     const total = subtotal + taxAmount;
     return {
       baseRate: displayRate,
       extraCharge,
       hasExtra: false, // Don't show separate extra lines
       isSubModalityPricing,
       effectiveRate: displayRate,
       billableHours,
       subtotal,
       taxAmount,
       total,
        isCustom: subModality?.includes("Customized") || false,
        isPremium: subModality?.includes("Premium") || subModality?.includes("Chef") || false,
     };
   }, [subModality, supplies, hours, persons, basePriceOverride, pricingConfiguration]);

  if (!hours || !subModality) return null;
  if (!priceDetails) return null;

  const pd = priceDetails;

  return (
    <Box
      className="feature-card shadow-none p-2 mb-2 mb-md-4"
      sx={{ bgcolor: "#fafafa", border: "1px solid #e0e0e0" }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, fontSize: "1rem", color: "#333" }}
        >
          Price Estimate
        </Typography>
        <Chip
          label={subModality}
          size="small"
          sx={{
            bgcolor: pd.isCustom
              ? "#f475a4"
              : pd.isPremium
              ? "#e3a0c5"
              : "#f0f0f0",
            color: pd.isCustom ? "#fff" : "#333",
            fontWeight: 600,
            fontSize: "0.7rem",
          }}
        />
      </Box>

      <Box sx={{ fontSize: "0.85rem", color: "#555" }}>
        {/* Individual Services + Package Comparison */}
        {servicesTotal && servicesTotal.services.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <span style={{ fontWeight: 600 }}>Individual Service Prices</span>
            </Box>
            {servicesTotal.services.map((svc: any, i: number) => (
              <Box key={i} display="flex" justifyContent="space-between" mb={0.3}>
                <span style={{ color: "#555" }}>{svc.name}</span>
                <span><strong>C$ {(svc.individualPrice * pd.billableHours).toLocaleString()} ({svc.individualPrice?.toLocaleString()}/hr × {pd.billableHours.toFixed(1)}h)</strong></span>
              </Box>
            ))}
            <Box display="flex" justifyContent="space-between" mb={0.5} sx={{ mt: 0.5 }}>
              <span style={{ fontWeight: 600 }}>Total (Individual)</span>
              <span><strong>C$ {(servicesTotal.total * pd.billableHours).toLocaleString()}</strong></span>
            </Box>

            <hr style={{ margin: "6px 0", border: "none", borderTop: "1px solid #ddd" }} />

            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <span>Package Price</span>
              <span><strong style={{ color: "#f475a4" }}>C$ {pd.subtotal.toFixed(2)}</strong></span>
            </Box>

            {(servicesTotal.total * pd.billableHours) > pd.subtotal && (
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <span style={{ fontWeight: 600, color: "#4caf50" }}>You Save</span>
                <span><strong style={{ color: "#4caf50" }}>C$ {((servicesTotal.total * pd.billableHours) - pd.subtotal).toFixed(2)}</strong></span>
              </Box>
            )}

            <hr style={{ margin: "6px 0", border: "none", borderTop: "1px solid #ddd" }} />
          </>
        )}

        {/* Rate breakdown — only show when no individual services */}
        {(!servicesTotal || servicesTotal.services.length === 0) && (
          <>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <span>Rate</span>
              <span><strong>C$ {pd.effectiveRate}/hr</strong></span>
            </Box>

            {pd.hasExtra && (
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <span>{pd.isSubModalityPricing ? "Extra Charge" : "Supplies Surcharge"}</span>
                <span><strong>+C$ {pd.extraCharge}/hr</strong></span>
              </Box>
            )}

            <hr style={{ margin: "6px 0", border: "none", borderTop: "1px solid #ddd" }} />
          </>
        )}

        {/* Hours & Persons */}
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <span>Duration ({hours}h × {persons} {persons > 1 ? 'persons' : 'person'})</span>
          <span><strong>{pd.billableHours.toFixed(1)} hrs</strong></span>
        </Box>

        <hr style={{ margin: "6px 0", border: "none", borderTop: "1px solid #ddd" }} />

        {/* Subtotal */}
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <span>Subtotal</span>
          <span><strong>C$ {pd.subtotal.toFixed(2)}</strong></span>
        </Box>

        {/* Tax */}
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <span>Tax (12%)</span>
          <span><strong>C$ {pd.taxAmount.toFixed(2)}</strong></span>
        </Box>

        <hr style={{ margin: "6px 0", border: "none", borderTop: "1px solid #ddd" }} />

        {/* Total */}
        <Box display="flex" justifyContent="space-between">
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "#222" }}>Total</span>
          <span style={{ fontWeight: 700, fontSize: "1rem", color: "#f475a4" }}>C$ {pd.total.toFixed(2)}</span>
        </Box>
      </Box>
    </Box>
  );
};

export default LivePriceCalculator;
